import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { getPublicItinerary } from "../../server/Api";
import "../../assets/css/GuestItinerary.css";

const formatDate = (value, options) => {
  if (!value) return "";
  const parsed = new Date(String(value).split("T")[0]);
  if (Number.isNaN(parsed.getTime())) return String(value).split("T")[0];
  return parsed.toLocaleDateString(
    undefined,
    options || {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    }
  );
};

const formatShortDate = (value) =>
  formatDate(value, { weekday: "short", day: "numeric", month: "short" });

const formatAddress = (address = {}) =>
  [address.line1, address.line2, address.city, address.state, address.pincode]
    .filter(Boolean)
    .join(", ");

const joinParts = (...parts) => parts.filter(Boolean).join(" · ");

const nightsLabel = (nights) => {
  const count = Number(nights);
  if (!Number.isFinite(count) || count < 1) return "";
  return `${count} ${count === 1 ? "night" : "nights"}`;
};

const formatSeatLabel = (seat) => {
  if (seat.tableNumber !== "" && seat.tableNumber != null) {
    return `Table ${seat.tableNumber}${
      seat.tableSeat !== "" && seat.tableSeat != null
        ? ` · Seat ${seat.tableSeat}`
        : ""
    }`;
  }
  if (seat.rowNumber !== "" && seat.rowNumber != null) {
    return `Row ${seat.rowNumber}${
      seat.rowSeat !== "" && seat.rowSeat != null
        ? ` · Seat ${seat.rowSeat}`
        : ""
    }`;
  }
  return seat.allocationType || "";
};

const GuestItinerary = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t") || searchParams.get("token") || "";
  const eventId = searchParams.get("eventId") || "";
  const regNo = searchParams.get("regNo") || "";
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Preparing your itinerary…");
  const [data, setData] = useState(null);

  const params = useMemo(() => {
    if (token) return { t: token };
    if (eventId && regNo) return { eventId, regNo };
    return null;
  }, [token, eventId, regNo]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!params) {
        if (!cancelled) {
          setStatus("error");
          setMessage(
            "This itinerary link is incomplete. Please use the link shared by your host."
          );
        }
        return;
      }

      try {
        const res = await axios.get(getPublicItinerary(), { params });
        if (cancelled) return;
        setData(res.data?.data || null);
        setStatus("ready");
      } catch (err) {
        if (cancelled) return;
        setStatus("error");
        setMessage(
          err?.response?.data?.message ||
            "We could not load this itinerary. Please contact the event host."
        );
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [params]);

  if (status === "loading" || status === "error") {
    return (
      <div className="gi-page">
        <div className="gi-status">
          <p className="gi-kicker">Your Itinerary</p>
          <h1>{status === "loading" ? "One moment" : "Link unavailable"}</h1>
          <p>{message}</p>
        </div>
      </div>
    );
  }

  const occasions = data?.occasions || [];
  const stay = data?.stay || [];
  const travel = data?.travel;
  const seats = data?.seats || [];
  const eventRange = joinParts(
    formatShortDate(data?.startDate),
    formatShortDate(data?.endDate)
      ? `to ${formatShortDate(data.endDate)}`
      : ""
  );
  const guestName = data?.person?.name || "Guest";

  return (
    <div className="gi-page">
      <div className="gi-sheet">
        <header className="gi-hero">
          <p className="gi-kicker">Your Itinerary</p>
          <h1>{data?.eventName || "Your celebration"}</h1>
          {eventRange ? <p className="gi-dates">{eventRange}</p> : null}
          <p className="gi-guest">{guestName}</p>
        </header>

        {occasions.length ? (
          <section className="gi-section is-occasions">
            <div className="gi-section-head">
              <p>01</p>
              <h2>Occasions</h2>
            </div>
            <ol className="gi-timeline">
              {occasions.map((occ, index) => (
                <li key={`${occ.occasionName}-${index}`}>
                  <div className="gi-timeline-when">
                    <strong>{formatShortDate(occ.date) || "Date to follow"}</strong>
                    {occ.day ? <span className="gi-day">{occ.day}</span> : null}
                    {occ.time ? <span>{occ.time}</span> : null}
                  </div>
                  <div className="gi-timeline-body">
                    <h3>{occ.occasionName}</h3>
                    <dl className="gi-meta">
                      {occ.venue ? (
                        <div>
                          <dt>Venue</dt>
                          <dd>{occ.venue}</dd>
                        </div>
                      ) : null}
                      {occ.venueAddress ? (
                        <div>
                          <dt>Address</dt>
                          <dd>{occ.venueAddress}</dd>
                        </div>
                      ) : null}
                      {occ.dressCode ? (
                        <div>
                          <dt>Dress code</dt>
                          <dd>{occ.dressCode}</dd>
                        </div>
                      ) : null}
                    </dl>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        ) : null}

        {stay.length ? (
          <section className="gi-section is-stay">
            <div className="gi-section-head">
              <p>02</p>
              <h2>Stay</h2>
            </div>
            <div className="gi-cards">
              {stay.map((item, index) => (
                <article key={`${item.hotelName}-${index}`} className="gi-card">
                  <div className="gi-card-title-row">
                    <h3>{item.hotelName || "Your hotel"}</h3>
                    {nightsLabel(item.nights) ? (
                      <span className="gi-chip is-nights">
                        {nightsLabel(item.nights)}
                      </span>
                    ) : null}
                  </div>
                  {item.roomType ? <p className="gi-chip">{item.roomType}</p> : null}
                  <dl>
                    <div>
                      <dt>Check-in</dt>
                      <dd>{joinParts(formatDate(item.checkInDate), item.checkInTime) || "—"}</dd>
                    </div>
                    <div>
                      <dt>Check-out</dt>
                      <dd>
                        {joinParts(formatDate(item.checkOutDate), item.checkOutTime) ||
                          "—"}
                      </dd>
                    </div>
                    {nightsLabel(item.nights) ? (
                      <div>
                        <dt>Nights</dt>
                        <dd>{nightsLabel(item.nights)}</dd>
                      </div>
                    ) : null}
                    {(item.roomNumbers || []).length ? (
                      <div>
                        <dt>Room</dt>
                        <dd>{item.roomNumbers.join(", ")}</dd>
                      </div>
                    ) : null}
                  </dl>
                  {formatAddress(item.address) ? (
                    <p className="gi-muted">{formatAddress(item.address)}</p>
                  ) : null}
                  {item.specialRequirements ? (
                    <p className="gi-note">{item.specialRequirements}</p>
                  ) : null}
                </article>
              ))}
            </div>
          </section>
        ) : null}

        {travel ? (
          <section className="gi-section is-travel">
            <div className="gi-section-head">
              <p>03</p>
              <h2>Travel</h2>
            </div>
            <div className="gi-travel">
              {(travel.arrivalDate ||
                travel.arrivalTime ||
                travel.arrivalMode ||
                travel.arrivalModeOfTravel ||
                travel.pickupRequired) && (
                <article className="gi-card">
                  <h3>Arrival</h3>
                  <p>
                    {joinParts(
                      formatDate(travel.arrivalDate),
                      travel.arrivalTime
                    ) || "Timing to follow"}
                  </p>
                  <p className="gi-muted">
                    {joinParts(
                      travel.arrivalMode || travel.arrivalModeOfTravel,
                      travel.arrivalFlightTrainBusNo,
                      travel.arrivalPNRNo ? `PNR ${travel.arrivalPNRNo}` : ""
                    )}
                  </p>
                  {travel.pickupRequired ? (
                    <p className="gi-note">
                      Pickup arranged
                      {travel.pickupLocation || travel.arrivalPickUpLocation
                        ? ` · ${travel.pickupLocation || travel.arrivalPickUpLocation}`
                        : ""}
                      {travel.pickupVehicle ? ` · ${travel.pickupVehicle}` : ""}
                    </p>
                  ) : null}
                </article>
              )}
              {(travel.departureDate ||
                travel.departureTime ||
                travel.departureMode ||
                travel.departureModeOfTravel ||
                travel.dropRequired) && (
                <article className="gi-card">
                  <h3>Departure</h3>
                  <p>
                    {joinParts(
                      formatDate(travel.departureDate),
                      travel.departureTime
                    ) || "Timing to follow"}
                  </p>
                  <p className="gi-muted">
                    {joinParts(
                      travel.departureMode || travel.departureModeOfTravel,
                      travel.departureFlightTrainBusNo,
                      travel.departurePNRNo ? `PNR ${travel.departurePNRNo}` : ""
                    )}
                  </p>
                  {travel.dropRequired ? (
                    <p className="gi-note">
                      Drop arranged
                      {travel.dropLocation || travel.departureDropLocation
                        ? ` · ${travel.dropLocation || travel.departureDropLocation}`
                        : ""}
                      {travel.dropVehicle ? ` · ${travel.dropVehicle}` : ""}
                    </p>
                  ) : null}
                </article>
              )}
            </div>
            {travel.itineraryNotes ? (
              <p className="gi-note gi-travel-notes">{travel.itineraryNotes}</p>
            ) : null}
          </section>
        ) : null}

        {seats.length ? (
          <section className="gi-section is-seating">
            <div className="gi-section-head">
              <p>04</p>
              <h2>Seating</h2>
            </div>
            <div className="gi-cards gi-cards-seat">
              {seats.map((seat, index) => (
                <article key={`${seat.occasionName}-${index}`} className="gi-card">
                  <h3>{seat.occasionName}</h3>
                  <p className="gi-muted">
                    {joinParts(formatShortDate(seat.date), seat.time, seat.venue)}
                  </p>
                  {formatSeatLabel(seat) ? (
                    <p className="gi-seat">{formatSeatLabel(seat)}</p>
                  ) : null}
                </article>
              ))}
            </div>
          </section>
        ) : null}

        {!occasions.length && !stay.length && !travel && !seats.length ? (
          <p className="gi-empty">
            Your host is still completing your arrangements. Please check back
            using this same link.
          </p>
        ) : null}

        <footer className="gi-footer">
          <p className="gi-update">Details will update if anything changes.</p>
          {data?.hostedBy ? (
            <p className="gi-hosted">Hosted by {data.hostedBy}</p>
          ) : null}
        </footer>
      </div>
    </div>
  );
};

export default GuestItinerary;
